/**
 * THE FRAME HALF of the silent renewal (see `auth/silentAuthorize.ts`).
 *
 * The hidden frame is pointed at Central's authorize endpoint, and Central
 * redirects back to BeHeard's REGISTERED redirect URI — which is BeHeard's own
 * `/login`. So the document that lands in the frame is this application.
 *
 * Without this module that document would boot a SECOND copy of the SPA inside
 * the frame, and `useCentralAuth`'s callback effect would consume the
 * single-use authorization code there, in a document that is about to be thrown
 * away, while the parent waited for a message that could never come. The
 * renewal would fail every time, and the code would be burned.
 *
 * So: when we are framed AND the `state` says this is a silent attempt, post the
 * authorize parameters to the parent and report `true`, which tells `main.tsx`
 * not to mount anything at all.
 *
 * ── WHY THE `state` PREFIX IS THE TEST, NOT "am I framed?" ───────────────
 *
 * An ordinary interactive login callback could also be framed one day (an
 * embedded sign-in, a preview pane, a test harness). Keying on the prefix means
 * only a callback this app's own silent flow started is diverted; anything else
 * boots normally and is handled by the existing callback path.
 *
 * ── SAFETY ───────────────────────────────────────────────────────────────
 *
 *   - `targetOrigin` is this document's own origin, never `'*'`: the parent is
 *     BeHeard, and an authorization code must not be broadcast to any other
 *     embedder. A frame embedded by a third party cannot receive it.
 *   - only the four authorize-response parameters are forwarded.
 *   - every failure path returns `false`, i.e. "boot normally". If this function
 *     cannot do its job the worst outcome is a renewal that times out — which
 *     the parent treats as an outage, never as a logout.
 */

import {
  SILENT_FORWARDED_PARAMS,
  SILENT_MESSAGE_SOURCE,
  SILENT_STATE_PREFIX,
  type SilentCallbackMessage,
} from './silentAuthorizeProtocol';

/**
 * The slice of `window` this needs. Structural, so the behaviour can be driven
 * in a package with no DOM.
 */
export interface SilentCallbackWindow {
  self: unknown;
  top: unknown;
  location: { search: string; origin: string };
  parent: { postMessage: (message: unknown, targetOrigin: string) => void };
}

/**
 * True when this document is the hidden silent-renewal frame and the answer has
 * been posted to the parent — in which case the caller must NOT mount the app.
 */
export function handleSilentCallbackIfFramed(win: SilentCallbackWindow): boolean {
  try {
    // Not framed: the ordinary application boot.
    if (win.top === win.self) return false;

    const search = win.location?.search ?? '';
    if (search === '') return false;

    const params = new URLSearchParams(search);
    const state = params.get('state');
    if (state === null || !state.startsWith(SILENT_STATE_PREFIX)) return false;

    const forwarded: Record<string, string> = {};
    for (const key of SILENT_FORWARDED_PARAMS) {
      const value = params.get(key);
      if (value !== null) forwarded[key] = value;
    }

    const message: SilentCallbackMessage = {
      source: SILENT_MESSAGE_SOURCE,
      state,
      params: forwarded,
    };

    win.parent.postMessage(message, win.location.origin);
    return true;
  } catch {
    // A hostile or unusual embedding (a cross-origin parent reference, a
    // throwing accessor). Boot normally rather than half-starting.
    return false;
  }
}
